import { useEffect, useMemo, useRef } from 'react'

import {
  AccessScreen,
  accessConsoleAction,
  accessRoleChanged,
  grantsOf,
  makeSelectAccessVm,
  platformAccessMatrix,
  platformMyAccess,
  platformResources,
  platformRoleDefaults,
  platformRoleResources,
  platformSaveRolePermissions,
  platformSaveRoleResources,
  SUPER_ROLE,
  type AccessActions,
} from '@krp/platform-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { CONNECTION_FORMS } from '../../composition/connectionForms'
import { useConnectors } from './useConnectors'
import { useMyAccess } from './useMyAccess'

const selectResources = platformResources.select()
const selectMatrix = platformAccessMatrix.select()

/**
 * Binding окна «Права к ресурсам». Запросы консоли идут только администратору
 * (иначе — экран с причиной без лишних 403); сервер проверяет каждую команду сам.
 */
export function AccessBinding() {
  const dispatch = useAppDispatch()
  const access = useMyAccess()
  const { registry: connectors } = useConnectors()
  const manage = accessConsoleAction(grantsOf(access)).allowed
  const role = useAppSelector((state: RootState) => state.platform.access.role)
  const roleArgs = useMemo(() => ({ role }), [role])

  // «Мои права» уже выбраны useMyAccess; своя подписка нужна, чтобы «Повторить» панели перечитывала именно их.
  const mySubscription = platformMyAccess.useQuerySubscription()
  const resourcesSubscription = platformResources.useQuerySubscription(undefined, { skip: !manage })
  const grantedSubscription = platformRoleResources.useQuerySubscription(roleArgs, { skip: !manage })
  const matrixSubscription = platformAccessMatrix.useQuerySubscription(undefined, { skip: !manage })
  // Стартовый набор роли (RP-FB5) — только подсказка галочкам; суперроли он не нужен.
  platformRoleDefaults.useQuerySubscription(roleArgs, { skip: !manage || role === SUPER_ROLE })
  const [saveResources, saveResourcesState] = platformSaveRoleResources.useMutation()
  const [savePermissions, savePermissionsState] = platformSaveRolePermissions.useMutation()

  // Итог сохранения относится к роли, для которой сохраняли: другая роль начинает с чистого листа.
  const resetResources = saveResourcesState.reset
  const resetPermissions = savePermissionsState.reset
  const shownRole = useRef(role)
  useEffect(() => {
    if (shownRole.current === role) return
    shownRole.current = role
    resetResources()
    resetPermissions()
  }, [role, resetPermissions, resetResources])

  const selectGranted = useMemo(() => platformRoleResources.select(roleArgs), [roleArgs])
  const selectDefaults = useMemo(() => platformRoleDefaults.select(roleArgs), [roleArgs])
  const defaults = useAppSelector((state: RootState) => selectDefaults(state))
  const resources = useAppSelector(selectResources)
  const granted = useAppSelector((state: RootState) => selectGranted(state))
  const matrix = useAppSelector(selectMatrix)
  const intent = useAppSelector((state: RootState) => state.platform.access)
  const selectVm = useMemo(() => makeSelectAccessVm(), [])
  const vm = selectVm({
    intent,
    access,
    resources,
    granted,
    matrix,
    defaults,
    forms: CONNECTION_FORMS,
    connectors,
    saveResources: saveResourcesState,
    savePermissions: savePermissionsState,
  })

  const refetchMine = mySubscription.refetch
  const refetchResources = resourcesSubscription.refetch
  const refetchGranted = grantedSubscription.refetch
  const refetchMatrix = matrixSubscription.refetch
  const actions = useMemo<AccessActions>(
    () => ({
      roleChanged: (next) => dispatch(accessRoleChanged(next)),
      saveResources: (resourceIds) => void saveResources({ role, resourceIds }),
      savePermissions: (permissions) => void savePermissions({ role, permissions }),
      reload: () => {
        void refetchMine()
        void refetchResources()
        void refetchGranted()
        void refetchMatrix()
      },
    }),
    [dispatch, refetchGranted, refetchMatrix, refetchMine, refetchResources, role, savePermissions, saveResources],
  )

  return <AccessScreen vm={vm} actions={actions} />
}
